import { applicationsApi, type RequestType } from 'api-validations/stitch'
import { ApiError, groupAndReduce, uniqueBy } from 'utils'

import { useOrgCodeOrThrow } from '../active-operation'
import { type ApiClient, panoramaQueryOptions, useApiClient, useQueries } from '../api-provider'
import { useStitchRequestType } from '../field-data/useStitchRequestType'

/**
 * Gets list of applications for a stream
 */
export const useApplications = (streams: string[]) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const requestType = useStitchRequestType()
  const queries = streams.map((streamId) =>
    applicationsQueryConfig({ orgCode, streamId, apiClient, requestType })
  )
  return useQueries({
    queries,
    combine: (results) =>
      results.reduce((combined, next) => {
        if (!next.isSuccess) return combined

        /* Extract unique applications (uniqueness determined by application id), and then for each
        duplicate application set, join extract the unique layers within it (uniqueness now determined
        by layer id) */
        return {
          ...next,
          data: groupAndReduce(
            next.data.concat(combined.data ?? []),
            (item) => item.application_id,
            (acc, next) => ({
              ...acc,
              layers: acc.layers.concat(next.layers).filter(uniqueBy((a, b) => a.id === b.id))
            })
          )
        }
      })
  })
}

/**
 * Same as useApplications, but for multiple streams.
 * The results are combined into an array of objects with the streamId and task.
 *
 * Note that each stream is queried separately, so they may not be ready at the same time.
 */
export const useMultiStreamApplications = (streams: ApplicationQueryParams[]) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const requestType = useStitchRequestType()

  const queries = streams.map(({ streamId }) =>
    applicationsQueryConfig({ orgCode, streamId, apiClient, requestType })
  )
  return useQueries({
    queries,
    combine: (result) =>
      result.map((d, i) => ({
        streamId: streams[i].streamId,
        data: d.data
      }))
  })
}

export { applicationTypes, COVERAGE_LAYER_ID, GLOBAL_APPLICATION_ID } from 'api-validations/stitch'

export type Application = NonNullable<ReturnType<typeof useApplications>['data']>[number]

type ApplicationsQueryOptions = {
  orgCode: string
  streamId: string
  apiClient: ApiClient
  requestType: RequestType
}

export const applicationsQueryConfig = ({ apiClient, ...params }: ApplicationsQueryOptions) => {
  const { queryFn, ...rest } = panoramaQueryOptions(applicationsApi, { apiClient, params })
  return {
    ...rest,
    // A stream that has been created but never processed has no applications
    // document yet, which the service reports as absent rather than as empty.
    queryFn: async () => {
      try {
        return await queryFn()
      } catch (e: unknown) {
        if (e instanceof ApiError && e.status === 404) return []
        throw e
      }
    }
  }
}

type ApplicationQueryParams = {
  streamId: string
}
